import { useEffect, useRef } from 'react';
import { hashKey, QueryKey } from '@tanstack/react-query';

type QueryEffectsSource<TData, TError> = {
  data: TData | undefined;
  error: TError | null;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
  isFetchedAfterMount: boolean;
};

export type QueryEffects<TData, TError> = {
  onSuccess?: (data: TData) => unknown;
  onError?: (error: TError) => unknown;
  onSettled?: (data: TData | undefined, error: TError | null) => unknown;
};

type Stamp = { dataUpdatedAt: number; errorUpdatedAt: number };

const stampOf = ({ dataUpdatedAt, errorUpdatedAt }: Stamp): Stamp => ({ dataUpdatedAt, errorUpdatedAt });

// Replaces the useQuery/useQueries onSuccess/onError/onSettled options that v5 removed. Like v3, the callbacks run
// once per mounted observer for every fetch of its current key that settles. isFetchedAfterMount resets when the key
// changes, so switching onto a key with cached data or a cached error does not count as a settled fetch.
export function useQueriesEffects<TData, TError>(
  queries: readonly QueryEffectsSource<TData, unknown>[],
  effects: readonly (QueryEffects<TData, TError> & { queryKey: QueryKey })[],
): void {
  const latest = useRef({ queries, effects });
  latest.current = { queries, effects };
  const identityOf = (index: number) => hashKey(latest.current.effects[index].queryKey);
  const stampsById = (current: readonly QueryEffectsSource<TData, unknown>[]) =>
    new Map(current.map((query, index) => [identityOf(index), stampOf(query)]));
  const seen = useRef<Map<string, Stamp> | null>(null);
  seen.current ??= stampsById(queries);
  const signature = queries.map((q) => `${q.dataUpdatedAt}:${q.errorUpdatedAt}:${q.isFetchedAfterMount}`).join();

  useEffect(() => {
    const { queries: current, effects: callbacks } = latest.current;
    current.forEach((query, index) => {
      const previous = seen.current?.get(identityOf(index)) ?? query;
      if (!query.isFetchedAfterMount) return;
      if (query.dataUpdatedAt !== previous.dataUpdatedAt && query.dataUpdatedAt > 0) {
        callbacks[index]?.onSuccess?.(query.data as TData);
        callbacks[index]?.onSettled?.(query.data, null);
      }
      if (query.errorUpdatedAt !== previous.errorUpdatedAt && query.error != null) {
        callbacks[index]?.onError?.(query.error as TError);
        callbacks[index]?.onSettled?.(undefined, query.error as TError);
      }
    });
    seen.current = stampsById(current);
  }, [signature]);
}

export function useQueryEffects<TData, TError>(
  query: QueryEffectsSource<TData, TError>,
  effects: QueryEffects<TData, TError>,
): void {
  useQueriesEffects([query], [{ ...effects, queryKey: [] }]);
}
